/* =========================================================================
   LAYER'D Toolkit — Production reminders (web push)
   File: assets/learn-reminders.js · loaded by learn-subnav.liquid (defer)

   A nudge three days before production starts, and again the evening
   before every work day. Optional morning-of digest, off unless asked for.

   WHY THIS EXISTS AT ALL, given the offline post-mortem: web push needs a
   service worker on the SHOP origin, theme assets are on cdn.shopify.com,
   and the app proxy at /apps/toolkit/ is the only same-origin door —
   which Shopify caps at /apps/toolkit/ scope by stripping
   Service-Worker-Allowed. That killed offline caching, which needed the
   worker to CONTROL /pages/*. A notification worker never does; it only
   needs an active registration. PROVEN ON HER IPHONE 2026-08-13.

   Division of labour with the Worker: this file does ALL the date maths
   and writes the finished notification text. The Worker stores an array
   of {at, title, body} and posts them when the clock says so. No
   timezone code runs server-side, so there is no second place for DST to
   be got wrong.

   Fails silent everywhere. If anything here throws, the Planner is
   exactly the Planner it was before this file existed.
   ========================================================================= */
(function () {
  'use strict';

  var DEVICE_KEY = 'layerd_reminders_device_id';
  var STATE_KEY = 'layerd_reminders_state';     /* {digest, lastSig, lastSyncAt} */
  var BASE = '/apps/toolkit/reminders';
  var SW_URL = '/apps/toolkit/push-sw.js';
  var HORIZON_DAYS = 90;
  var MAX_ENTRIES = 200;
  var SYNC_DEBOUNCE = 2000;

  var host = null;      /* the FULL card's slot — the settings panel or its modal */
  var plannerHost = null;   /* the compact door's slot on the Planner (see mountPoint) */
  var reg = null;
  var sub = null;
  var feed = null;
  var lastErr = '';
  var syncNote = '';
  var syncTone = '';
  var syncTimer = null;

  /* ---------- tiny DOM helper, same shape as learn-components.js ------- */
  function el(tag, attrs, kids) {
    var n = document.createElement(tag);
    attrs = attrs || {};
    for (var k in attrs) {
      if (!Object.prototype.hasOwnProperty.call(attrs, k)) continue;
      if (k === 'text') n.textContent = attrs[k];
      else if (k === 'class') n.className = attrs[k];
      else n.setAttribute(k, attrs[k]);
    }
    (kids || []).forEach(function (c) { if (c) n.appendChild(c); });
    return n;
  }
  function clear(n) { while (n.firstChild) n.removeChild(n.firstChild); }

  /* ---------- environment ---------------------------------------------- */
  var isStandalone =
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true;
  var isIOS = /iP(hone|od|ad)/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  function tier() {
    var n = document.querySelector('[data-lh-tier]');
    return (n && n.getAttribute('data-lh-tier')) || 'visitor';
  }
  function iconUrl() {
    var n = document.querySelector('link[rel="apple-touch-icon"]') ||
      document.querySelector('link[rel="icon"]');
    return (n && n.href && n.href.slice(0, 8) === 'https://') ? n.href : '';
  }
  function deviceId() {
    try {
      var v = localStorage.getItem(DEVICE_KEY);
      if (v) return v;
      v = (window.crypto && crypto.randomUUID) ? crypto.randomUUID()
        : 'd' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      localStorage.setItem(DEVICE_KEY, v);
      return v;
    } catch (e) { return 'nostorage'; }
  }
  function state() {
    try { return JSON.parse(localStorage.getItem(STATE_KEY)) || {}; } catch (e) { return {}; }
  }
  function saveState(patch) {
    try {
      var s = state();
      for (var k in patch) if (Object.prototype.hasOwnProperty.call(patch, k)) s[k] = patch[k];
      localStorage.setItem(STATE_KEY, JSON.stringify(s));
    } catch (e) { }
  }
  function errText(e) {
    if (!e) return '';
    if (e.name || e.message) return (e.name || 'Error') + ': ' + (e.message || '');
    return String(e);
  }
  function b64uToBytes(s) {
    var t = String(s).replace(/-/g, '+').replace(/_/g, '/');
    while (t.length % 4) t += '=';
    var bin = atob(t);
    var out = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }

  /* =====================================================================
     THE SCHEDULE — a pure function, so it can be tested without a
     browser permission, a service worker or a network.
     ===================================================================== */

  function pad2(n) { return n < 10 ? '0' + n : '' + n; }

  /* Calendar-day arithmetic in UTC on purpose: UTC has no DST, so adding
     -3 days can never land on 23:00 the day before. */
  function shiftIso(iso, delta) {
    var t = Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) + delta * 86400000;
    var d = new Date(t);
    return d.getUTCFullYear() + '-' + pad2(d.getUTCMonth() + 1) + '-' + pad2(d.getUTCDate());
  }

  /* What is this instant's offset from UTC in the named zone? Format it
     there, read the wall clock back as if it were UTC, and the gap IS the
     offset. Intl is the only thing in the platform that knows the rules. */
  function tzOffsetMinutes(tz, ms) {
    var dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
    var m = {};
    dtf.formatToParts(new Date(ms)).forEach(function (p) { m[p.type] = p.value; });
    var asUtc = Date.UTC(+m.year, +m.month - 1, +m.day,
      +m.hour === 24 ? 0 : +m.hour, +m.minute, +m.second);
    return (asUtc - ms) / 60000;
  }

  /* "6pm on 2026-11-01 in America/Toronto" -> the real UTC instant.
     Two passes: the first offset is a guess made at the wrong instant, and
     on the two DST days a year that guess is an hour out. Re-reading the
     offset AT the computed instant settles it. */
  function localToUtc(iso, h, min, tz) {
    var wall = Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10), h, min, 0);
    var off = tzOffsetMinutes(tz, wall);
    var ms = wall - off * 60000;
    var off2 = tzOffsetMinutes(tz, ms);
    if (off2 !== off) ms = wall - off2 * 60000;
    return ms;
  }

  function weekday(iso) {
    try {
      return new Date(iso + 'T12:00:00Z')
        .toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
    } catch (e) { return 'soon'; }
  }
  function lower1(s) { return s ? s.charAt(0).toLowerCase() + s.slice(1) : s; }
  function upper1(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
  /* iOS lock screens cut a body around 120 characters; cut it ourselves at
     a word boundary so it ends as a sentence rather than mid-word. */
  function clip(s, max) {
    if (!s || s.length <= max) return s;
    var cut = s.slice(0, max - 1);
    var sp = cut.lastIndexOf(' ');
    return (sp > max * 0.6 ? cut.slice(0, sp) : cut) + '…';
  }

  function summarise(items) {
    if (items.length === 1) return lower1(items[0].label) + ' for ' + items[0].name + '.';
    if (items.length === 2) {
      return lower1(items[0].label) + ' for ' + items[0].name + ' + ' +
        lower1(items[1].label) + ' for ' + items[1].name + '.';
    }
    var n = 0;
    items.forEach(function (i) { n += i.n || 0; });
    return n + ' tasks across ' + items.length + ' cakes.';
  }

  function nameList(items) {
    var names = items.map(function (i) { return i.name; });
    if (names.length === 2) return names[0] + ' and ' + names[1];
    return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  }

  function compose(moment) {
    var items = moment.items, title, body;
    if (moment.kind === 'headsup') {
      var day = weekday(items[0].when);
      if (items.length === 1) {
        title = 'Production starts ' + day;
        body = 'Production for ' + items[0].name + ' starts ' + day + '. Tap to review.';
      } else {
        title = items.length + ' cakes start ' + day;
        body = nameList(items) + (items.length === 2 ? ' both start ' : ' all start ') +
          'production ' + day + '. Tap to review.';
      }
      /* Lead with the blocker: announcing production on a cake that still
         has no flavours assigned would be cheerfully wrong. */
      var blocked = items.filter(function (i) { return i.provText; });
      if (blocked.length) body = upper1(blocked[0].provText) + ' still needed — ' + lower1(body);
    } else {
      title = moment.kind === 'evening' ? 'Tomorrow in the studio' : 'Today in the studio';
      body = (moment.kind === 'evening' ? 'Tomorrow: ' : 'Today: ') + summarise(items);
    }
    return {
      at: new Date(moment.at).toISOString(),
      kind: moment.kind,
      title: clip(title, 64),
      body: clip(body, 120),
      /* one cake -> open that file; several -> the Planner itself */
      url: items.length === 1 && items[0].id
        ? '/pages/planner?file=' + encodeURIComponent(items[0].id)
        : '/pages/planner',
      /* tag = the send moment, so a reminder replaces its earlier self on
         the lock screen instead of stacking */
      tag: moment.kind + ':' + moment.at
    };
  }

  function buildReminderSchedule(f, opts) {
    opts = opts || {};
    var tz = opts.tz || 'UTC';
    var now = opts.now || Date.now();
    var horizon = now + (opts.horizonDays || HORIZON_DAYS) * 86400000;
    var moments = {};

    function add(kind, ms, item) {
      /* Past-due suppression: a booking entered two days before production
         simply skips its heads-up and starts at the evening-before. */
      if (!(ms > now) || ms > horizon) return;
      var k = kind + '|' + ms;
      if (!moments[k]) moments[k] = { at: ms, kind: kind, items: [] };
      moments[k].items.push(item);
    }

    var days = Object.keys((f && f.days) || {}).sort();
    var cakes = (f && f.cakes) || {};

    Object.keys(cakes).forEach(function (id) {
      var c = cakes[id];
      if (!c || !c.firstIso) return;
      add('headsup', localToUtc(shiftIso(c.firstIso, -3), 9, 0, tz),
        { id: id, name: c.name, when: c.firstIso, provText: c.provText || '' });
    });

    days.forEach(function (iso) {
      var eveMs = localToUtc(shiftIso(iso, -1), 18, 0, tz);
      var dayMs = opts.digest ? localToUtc(iso, 7, 30, tz) : 0;
      (f.days[iso].items || []).forEach(function (it) {
        var payload = { id: it.id, name: it.name, label: it.label, n: it.n, when: iso };
        add('evening', eveMs, payload);
        if (opts.digest) add('digest', dayMs, payload);
      });
    });

    return Object.keys(moments)
      .map(function (k) { return compose(moments[k]); })
      .sort(function (a, b) { return a.at < b.at ? -1 : a.at > b.at ? 1 : 0; })
      .slice(0, opts.max || MAX_ENTRIES);
  }

  function currentTz() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
    catch (e) { return 'UTC'; }
  }

  /* =====================================================================
     STATE + CARD
     ===================================================================== */
  /* Order matters: on iOS PushManager is simply absent outside an
     installed app, so "you need to install this" must be tested before
     "your browser can't do this" — otherwise every iPhone in Safari reads
     as an unsupported browser, which is a lie. */
  function detect() {
    if (isIOS && !isStandalone) return 'needs-install';
    if (!('serviceWorker' in navigator) || !('PushManager' in window) ||
      !('Notification' in window)) return 'unsupported';
    if (Notification.permission === 'denied') return 'blocked';
    if (Notification.permission === 'granted' && sub) return 'enabled';
    return 'available';
  }

  function row(label, hint) {
    return el('div', { class: 'ld-settings__row ld-settings__row--data' }, [
      el('div', { class: 'ld-settings__text' }, [
        el('span', { class: 'ld-settings__lbl', text: label }),
        hint ? el('span', { class: 'ld-settings__hint', text: hint }) : null
      ])
    ]);
  }

  /* .ld-btn, not .ld-settings__databtn: those measure 34px tall, under the
     touch target the M1 mobile pass set. The shared button scale gives
     44px (--md) and 40px (--sm) with no new CSS. */
  function button(text, variant, onClick) {
    var b = el('button', {
      type: 'button',
      class: 'ld-btn ' + (variant === 'primary' ? 'ld-btn--primary ld-btn--md' : 'ld-btn--secondary ld-btn--sm'),
      text: text
    });
    b.addEventListener('click', onClick);
    return b;
  }

  function digestToggle() {
    var on = !!state().digest;
    var wrap = el('div', { class: 'ld-settings__row' }, [
      el('div', { class: 'ld-settings__text' }, [
        el('span', { class: 'ld-settings__lbl', text: 'Also remind me each morning' }),
        el('span', { class: 'ld-settings__hint', text: '7:30am on days you have work. Off unless you want it.' })
      ])
    ]);
    var seg = el('div', { class: 'ld-settings__seg' });
    [{ v: false, t: 'Off' }, { v: true, t: 'On' }].forEach(function (o) {
      var b = el('button', {
        type: 'button',
        class: 'ld-settings__segb' + (on === o.v ? ' is-on' : ''),
        text: o.t
      });
      b.addEventListener('click', function () {
        saveState({ digest: o.v });
        /* the schedule changes shape, so re-send it now rather than
           waiting for her next Planner edit */
        syncNow(true);
        repaint();
      });
      seg.appendChild(b);
    });
    wrap.appendChild(seg);
    return wrap;
  }

  function scheduledThrough() {
    var days = feed && feed.days ? Object.keys(feed.days).sort() : [];
    if (!days.length) return '';
    var last = days[days.length - 1];
    try {
      return new Date(last + 'T12:00:00Z')
        .toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' });
    } catch (e) { return ''; }
  }

  function render(st, note, noteTone) {
    if (!host) return;
    clear(host);
    var card = el('div', { class: 'ld-settings' });

    card.appendChild(el('div', { class: 'ld-settings__head' }, [
      el('span', { class: 'ld-settings__eyebrow', text: 'Production reminders' }),
      el('span', {
        class: 'ld-settings__sub',
        text: 'A nudge three days before production starts, and again the evening before each work day.'
      })
    ]));

    var btns = el('div', { class: 'ld-settings__databtns' });

    if (st === 'needs-install') {
      card.appendChild(row('Add the Toolkit to your Home Screen first',
        'iPhones only allow reminders from an installed app — Share, then Add to Home Screen. Open the Planner from that icon and this card will offer to turn them on.'));

    } else if (st === 'unsupported') {
      card.appendChild(row('This browser can’t do reminders',
        'Nothing is wrong with your files — this device just has no notification support. Try the installed app on your phone.'));

    } else if (st === 'blocked') {
      card.appendChild(row('Notifications are switched off for the Toolkit',
        'Your device is refusing them, so nothing here can ask again. Settings → Notifications → LAYER’D Toolkit to allow them, then come back.'));

    } else if (st === 'enabled') {
      var through = scheduledThrough();
      card.appendChild(row('Reminders are on for this device',
        through
          ? 'Lovely — we’ll keep you on track. Scheduled through ' + through + ', and they follow every change you make here.'
          : 'Lovely — we’ll keep you on track. Nothing to remind you about yet: book a cake and the schedule builds itself.'));
      card.appendChild(digestToggle());
      btns.appendChild(button('Turn off reminders', 'secondary', doDisable));
      card.appendChild(btns);

    } else {
      card.appendChild(row('Want a nudge before production starts?',
        'We’ll ask your phone for permission once. You can turn them off here at any time.'));
      btns.appendChild(button('Turn on reminders', 'primary', doEnable));
      card.appendChild(btns);
    }

    var text = note || syncNote;
    var tone = note ? noteTone : syncTone;
    if (text) {
      card.appendChild(el('p', {
        class: 'ld-settings__datanote' + (tone ? ' is-' + tone : ''), text: text
      }));
    }
    /* 2026-09-11: the raw error detail used to render here as visible text,
       prefixed "Details for Claude:" - internal dev copy leaked to the user.
       Stripped; the diagnostic string is kept only in the console. */
    if (lastErr) { try { console.warn('[reminders]', lastErr); } catch (e) {} }
    host.appendChild(card);
  }

  /* UX 2026-09-14 (Mariya: "the production reminders should just redirect them
     to the settings popup instead of asking all the same questions again").
     On the Planner the card is now a compact DOOR, not the full setup — the
     enable / turn-off / morning-digest controls live in the settings popup
     (the same card, mounted there), and this points to it. `.ld-setopen` is the
     suite-wide hook that opens that popup in place; the href is the no-JS
     fallback, matching the settings gear. One state-aware line, no questions. */
  function renderDoor(slot) {
    if (!slot) return;
    clear(slot);
    var st = detect();
    var line;
    if (st === 'enabled') line = 'Reminders are on for this device.';
    else if (st === 'blocked') line = 'Notifications are switched off for the Toolkit.';
    else if (st === 'needs-install') line = 'Add the Toolkit to your Home Screen to turn these on.';
    else if (st === 'unsupported') line = 'This device has no notification support.';
    else line = 'A nudge before each production day.';
    var card = el('div', { class: 'ld-settings ld-rem-door' });
    card.appendChild(el('div', { class: 'ld-settings__head' }, [
      el('span', { class: 'ld-settings__eyebrow', text: 'Production reminders' }),
      el('span', { class: 'ld-settings__sub', text: line })
    ]));
    card.appendChild(el('a', {
      class: 'ld-settings__report ld-setopen', href: '/pages/studio?ldsp=account',
      text: 'Manage reminders in settings →'
    }));
    slot.appendChild(card);
  }

  function repaint(note, tone) {
    render(detect(), note, tone);   /* the full card, when its slot exists */
    renderDoor(plannerHost);        /* the Planner door, when it exists */
  }

  /* =====================================================================
     WORKER CALLS
     ===================================================================== */
  function proxy(path, opts) {
    return fetch(path, opts).then(function (res) {
      if (res.status === 401 || res.status === 403) {
        var e = new Error('you are logged out on this device');
        e.name = 'LoggedOut'; throw e;
      }
      if (res.status === 404) {
        var e2 = new Error('the /apps/toolkit proxy did not answer');
        e2.name = 'NoProxy'; throw e2;
      }
      return res.json().catch(function () { return {}; }).then(function (body) {
        if (!res.ok) {
          var e3 = new Error((body && body.error) || ('HTTP ' + res.status));
          e3.name = 'Server' + res.status; throw e3;
        }
        return body;
      });
    });
  }

  /* register() resolves before the worker is necessarily ACTIVE, and
     pushManager.subscribe() on a registration with no active worker throws
     InvalidStateError. Wait properly rather than hoping. */
  function whenActive(r) {
    if (r.active) return Promise.resolve(r);
    var sw = r.installing || r.waiting;
    if (!sw) {
      /* NOT navigator.serviceWorker.ready — that resolves with the
         registration CONTROLLING this page, and the push worker at
         /apps/toolkit/ scope never controls /pages/*, so it would wait
         forever with no timeout. Poll the registration itself instead. */
      return new Promise(function (resolve, reject) {
        var waited = 0;
        (function poll() {
          if (r.active) return resolve(r);
          waited += 200;
          if (waited >= 10000) {
            var e = new Error('the reminder worker never finished starting');
            e.name = 'ActivateTimeout'; return reject(e);
          }
          setTimeout(poll, 200);
        })();
      });
    }
    return new Promise(function (resolve, reject) {
      var done = false;
      var t = setTimeout(function () {
        if (done) return;
        done = true;
        var e = new Error('the reminder worker never finished starting');
        e.name = 'ActivateTimeout'; reject(e);
      }, 10000);
      sw.addEventListener('statechange', function () {
        if (done || sw.state !== 'activated') return;
        done = true; clearTimeout(t); resolve(r);
      });
    });
  }

  function subscribeNow() {
    return proxy(BASE + '/key', { credentials: 'same-origin' }).then(function (body) {
      if (!body || !body.key) throw new Error('the server sent no key');
      /* No { scope } option on purpose: the script lives at /apps/toolkit/,
         so that IS its scope, and asking for a wider one is what threw
         SecurityError in the July offline spike. */
      return navigator.serviceWorker.register(SW_URL)
        .then(function (r) { reg = r; return whenActive(r); })
        .then(function (r) {
          return r.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: b64uToBytes(body.key)
          });
        });
    });
  }

  function syncNow(force) {
    if (!sub) return Promise.resolve(null);
    /* No feed yet is NOT an empty schedule. On load this runs before the
       Planner has published anything; syncing then would push an empty
       schedule over the server's real one, and a close inside that window
       would strand her reminderless until the next visit. A genuinely
       empty week still syncs fine — it arrives as a feed OBJECT with no
       days, via the ldtl:remfeed event. */
    if (!feed) return Promise.resolve(null);
    var s = state();
    var schedule = buildReminderSchedule(feed, {
      tz: currentTz(), now: Date.now(), digest: !!s.digest
    });
    var payload = {
      deviceId: deviceId(),
      subscription: sub.toJSON(),
      tz: currentTz(),
      digest: !!s.digest,
      icon: iconUrl(),
      schedule: schedule
    };
    /* Dedupe like cloud sync does: a page load that changes nothing should
       not burn a write. */
    var sig = JSON.stringify([payload.tz, payload.digest, schedule]);
    if (!force && sig === s.lastSig) return Promise.resolve(null);

    return proxy(BASE + '/schedule', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (body) {
      saveState({ lastSig: sig, lastSyncAt: Date.now() });
      syncNote = ''; syncTone = '';
      lastErr = '';
      repaint();
      return body;
    }).catch(function (e) {
      /* Round 42's doctrine: a write that fails must SAY SO. A reminder
         schedule that silently stopped updating is exactly the kind of
         quiet failure that costs a cake. */
      lastErr = errText(e);
      syncTone = 'warn';
      /* "Will retry on your next change" is only true when a retry can
         succeed. A 413 refuses the same payload every time — saying it
         would retry is the false promise round 42 exists to forbid. */
      syncNote = (e && e.name === 'LoggedOut')
        ? 'Reminders already set will still arrive, but they’ll stop updating until you log in on this device.'
        : (e && e.name === 'Server413')
          ? 'Your schedule is larger than the server allows, so it won’t sync, but it’s still saved in this browser.'
          : 'Couldn’t send your updated schedule — it will retry on your next change.';
      repaint();
      return null;
    });
  }

  function queueSync() {
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(function () { syncNow(false); }, SYNC_DEBOUNCE);
  }

  function doEnable() {
    lastErr = '';
    repaint('Asking your device…');
    /* Must be called from inside this tap: iOS only accepts a permission
       request during a user gesture, and a native "Don't Allow" is
       permanent — which is why this never runs on page load. */
    var ask;
    try { ask = Notification.requestPermission(); } catch (e) { ask = null; }
    if (!ask || typeof ask.then !== 'function') {
      ask = new Promise(function (resolve) { Notification.requestPermission(resolve); });
    }
    ask.then(function (perm) {
      if (perm !== 'granted') { repaint('No problem — nothing was turned on.', 'warn'); return null; }
      return subscribeNow();
    }).then(function (s) {
      if (!s) return null;
      sub = s;
      repaint();
      return syncNow(true);
    }).then(function (body) {
      if (!body) return;
      repaint(body.count
        ? 'Reminders are on — ' + body.count + ' scheduled.'
        : 'Reminders are on.', 'ok');
    }).catch(function (e) {
      lastErr = errText(e);
      var msg = 'Reminders couldn’t be turned on.';
      if (e && e.name === 'LoggedOut') msg = 'Log in on this device first — reminders are tied to your account.';
      else if (e && e.name === 'NoProxy') msg = 'The Toolkit server didn’t answer. Nothing is broken in your files.';
      repaint(msg, 'warn');
    });
  }

  function doDisable() {
    lastErr = ''; syncNote = ''; syncTone = '';
    var id = deviceId();
    var p = sub ? sub.unsubscribe().catch(function () { return true; }) : Promise.resolve(true);
    p.then(function () {
      return proxy(BASE + '/unsubscribe', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId: id })
      }).catch(function () { return null; });   /* local off is what matters */
    }).then(function () {
      sub = null;
      saveState({ lastSig: '' });
      repaint('Reminders are off for this device.', '');
    });
  }

  /* =====================================================================
     BOOT
     ===================================================================== */
  function mountPoint() {
    var app = document.getElementById('tlhub-app');
    if (!app) return null;
    /* A sibling of #tlhub-app, never a child: the Planner replaces that
       element's contents on every render, and a card inside it would
       vanish the first time she switched view. */
    var slot = document.getElementById('ld-reminders-door');
    if (slot) return slot;
    slot = el('div', { id: 'ld-reminders-door', style: 'margin-top:1.25rem;' });
    if (app.parentNode) app.parentNode.insertBefore(slot, app.nextSibling);
    return slot;
  }

  /* The card belongs with the schedule it describes, so it shows on the
     Production view. The hub's Toolkit settings carries a status row for
     everyone who never opens that tab. */
  function applyVisibility() {
    if (!plannerHost) return;
    plannerHost.hidden = !(feed && feed.view === 'production');
  }

  /* Find the subscription this device already holds, if any. Split from
     adoptExisting because the HUB needs the answer too: its status row
     once rendered straight from detect(), which reads the in-memory `sub`
     — always null on a page that never probed — so the row said "Off" to
     a member whose reminders were on (her live find, 2026-08-13). Probe
     first, then say. */
  function probeSub() {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.getRegistration) {
      return Promise.resolve(null);
    }
    return navigator.serviceWorker.getRegistration(SW_URL).then(function (r) {
      if (!r || !r.pushManager) return null;
      reg = r;
      return r.pushManager.getSubscription().then(function (s) {
        sub = s || null;
        return sub;
      });
    }).catch(function () { return null; });
  }

  function adoptExisting() {
    probeSub().then(function () {
      /* Resubscription healing: permission is still granted but the OS
         dropped the subscription (expiry, restore from backup). Quietly
         take a new one — she never asked for an interruption. */
      if (!sub && reg && Notification.permission === 'granted') {
        return subscribeNow().then(function (s2) { sub = s2; }).catch(function () { });
      }
      return null;
    }).then(function () {
      repaint();
      /* heal drift between this device and the server on every load */
      if (sub) syncNow(false);
    }).catch(function () { repaint(); });
  }

  /* The hub's Toolkit settings gets a one-line status, for anyone who
     never opens the Production view. INJECTED into the existing card
     rather than added to learn-components.js — that file is shared by
     five tools, and this keeps it out of the deploy list entirely. Same
     reasoning as the settings gear, which injects itself for the same
     reason. */
  /* UX 2026-09-09 (Mariya: "include the push notification options in the
     settings"): on any page WITHOUT the Planner, the settings panel gets the
     FULL reminders card — the same Turn on / Turn off / morning-digest controls
     the Planner's Production view has — not just a status row pointing there.
     The card's slot becomes `host`, so every re-render after a tap lands in it. */
  function mountSettingsCard() {
    /* 2026-09-14: the Planner used to be skipped here (it kept its own full
       card). Now the Planner shows only a DOOR, and the full controls belong to
       the settings popup — so this mounts even on the Planner, into the modal
       the door opens. `host` is the modal's slot; the door is `plannerHost`. */
    var panel = document.querySelector('[data-ld-settings] .ld-settings');
    if (!panel) return false;                                 /* not built yet — retry */
    if (panel.querySelector('#ld-reminders')) return true;
    var slot = el('div', { id: 'ld-reminders', class: 'ld-rem-slot' });
    /* before the quiet "Report a problem" link, which stays last */
    var report = panel.querySelector('.ld-settings__report');
    if (report && report.parentNode === panel) panel.insertBefore(slot, report);
    else panel.appendChild(slot);
    host = slot;
    render(detect());
    return true;
  }

  /* below Pro the feature is a locked row — so free members learn it exists */
  function mountLockedRow() {
    var panel = document.querySelector('[data-ld-settings] .ld-settings');
    if (!panel) return false;
    if (panel.querySelector('.ld-rem-statusrow')) return true;
    var rowEl = el('div', { class: 'ld-settings__row ld-rem-statusrow' }, [
      el('div', { class: 'ld-settings__text' }, [
        el('span', { class: 'ld-settings__lbl', text: 'Production reminders' }),
        el('span', { class: 'ld-settings__hint', text: 'A push nudge before each production day. Part of Toolkit Pro.' })
      ]),
      el('a', { class: 'ld-settings__report', style: 'margin-left:auto;', href: '/pages/toolkit', text: 'What Pro unlocks →' })
    ]);
    var report = panel.querySelector('.ld-settings__report');
    if (report && report.parentNode === panel) panel.insertBefore(rowEl, report);
    else panel.appendChild(rowEl);
    return true;
  }

  function boot() {
    try {
      var pro = tier() === 'premium';
      /* Settings-panel path (the hub, and the Studio's Settings tab): the panel
         may build later — the Studio only renders it when the tab is opened —
         so besides a short retry we listen for the Studio's mount event.
         PROBE BEFORE MOUNTING — the card's state comes from detect(), and
         detect() can only see a subscription that has been looked up. */
      function tryMount() { if (pro) probeSub().then(mountSettingsCard); else mountLockedRow(); }
      window.addEventListener('ld:settings-mounted', tryMount);
      if (!document.getElementById('tlhub-app')) {
        tryMount();
        [400, 1200, 2500].forEach(function (ms) { setTimeout(tryMount, ms); });
        return;
      }
      if (!pro) return;

      plannerHost = mountPoint();
      if (!plannerHost) return;
      plannerHost.hidden = true;
      renderDoor(plannerHost);

      window.addEventListener('ldtl:remfeed', function (ev) {
        feed = (ev && ev.detail) || feed;
        applyVisibility();
        repaint();
        if (sub) queueSync();
      });

      var eng = window.LAYERD && window.LAYERD.timeline;
      if (eng && eng.feed) { feed = eng.feed; applyVisibility(); }

      adoptExisting();
    } catch (e) { /* a reminder card must never cost anyone the Planner */ }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.LAYERD = window.LAYERD || {};
  window.LAYERD.reminders = {
    detect: detect,
    /* exported for the harness — pure, no browser state involved */
    buildReminderSchedule: buildReminderSchedule,
    localToUtc: localToUtc,
    shiftIso: shiftIso,
    status: function () { return { subscribed: !!sub, digest: !!state().digest, lastSyncAt: state().lastSyncAt || 0 }; },
    __mount: function (node) { host = node; },
    __render: render,
    __feed: function (f) { feed = f; }
  };
})();
/* LDREM-EOF v3 - if this line is missing from the SERVED file, the upload was truncated and the whole script is dead. */
